import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { isAxiosError } from 'axios';
import * as Device from 'expo-device';
import Constants from 'src/core/utils/Constants';
import Translations from 'src/core/utils/Translations';
import { isApiValidationErrorResponse } from 'src/core/models/api/ApiValidationErrorResponse';
import LoginFlowType from 'src/domain/models/LoginFlowType';
import LoginStepType from 'src/domain/models/LoginStepType';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { usePreferencesStore } from 'src/domain/stores/usePreferencesStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import type ApiErrorResponse from 'src/core/models/api/responses/ApiErrorResponse';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';
import type ApiUser from 'src/core/models/api/ApiUser';

const initialState = {
	email: '',
	password: '',
	gender: '',
	firstName: '',
	infix: '',
	lastName: '',
	newPassword: '',
	repeatPassword: '',
	step: LoginStepType.Email,
	flow: LoginFlowType.Login,
	isLoading: false,
	error: null as ApiErrorResponse | null,
	isResetPasswordProcessing: false,
	isResendVerificationProcessing: false,
	resetPasswordSuccessful: null as boolean | null,
	isResetPasswordDialogVisible: false,
	isRegistrationDoneDialogVisible: false,
	isMailSentDialogVisible: false,
	isVerificationDialogVisible: false,
};

const validationErrorOf = (error: ApiRequestError): ApiErrorResponse | null =>
	isAxiosError(error) &&
	error.response?.status === Constants.httpStatusUnprocessableEntity &&
	isApiValidationErrorResponse(error.response.data)
		? error.response.data
		: null;

const messageOf = (error: ApiRequestError): string | null => {
	const message = isAxiosError(error) ? error.response?.data?.message : undefined;

	return message !== undefined && message !== '' ? message : null;
};

const statusOf = (error: ApiRequestError): number | undefined =>
	isAxiosError(error) ? error.response?.status : undefined;

export const useLoginStore = create(
	combine({ ...initialState }, (set, get) => {
		const init = () => set({ ...initialState });

		const _applyLanguagePreference = async () => {
			await usePreferencesStore.getState().getApiPreferences({
				onSuccess: async (response) => {
					const selectedLanguage = response.data?.preferred_language.find((language) => language.selected);

					if (selectedLanguage) {
						await useAuthStore.getState().setLanguage(selectedLanguage.id);
					}
				},
				onError: (error) =>
					useServicesStore.getState().captureEvent({ message: 'getApiPreferences failed after login', extra: { error } }),
			});
		};

		const _openSession = async ({ member, accessToken }: { member: ApiUser; accessToken: string }) => {
			const auth = useAuthStore.getState();

			await auth.setUser(member);
			useServicesStore.getState().logEvent({ name: Constants.analyticsEventLogin, params: {}, user: member });
			await auth.setDidOnboarding(true);
			await auth.setToken(accessToken);
			await _applyLanguagePreference();
		};

		const postCheckEmail = async ({
			onSuccess,
			onRegister,
		}: {
			onSuccess?: () => void;
			onRegister?: () => void;
		} = {}) => {
			const email = get().email;

			if (!useServicesStore.getState().isValidEmail({ email })) {
				set({ error: { message: Translations.emailAddressInvalid } });

				return;
			}

			set({ isLoading: true });

			await useAuthStore.getState().postApiCheckEmail({
				email,
				onSuccess: () => {
					set({ isLoading: false, flow: LoginFlowType.Login });
					onSuccess?.();
				},
				onError: (error) => {
					set({ isLoading: false });

					const validationError = validationErrorOf(error);
					const message = statusOf(error) === Constants.httpStatusUnprocessableEntity ? messageOf(error) : null;

					if (validationError) {
						set({ error: validationError });
					} else if (message) {
						set({ error: { message } });
					} else if (statusOf(error) === Constants.httpStatusNotFound) {
						set({ flow: LoginFlowType.Register });
						onRegister?.();
					}
				},
			});
		};

		const postLogin = async ({ onRejected }: { onRejected?: () => void } = {}) => {
			set({ isLoading: true });

			await useAuthStore.getState().postApiLogin({
				email: get().email,
				password: get().password,
				deviceName: Device.deviceName ?? Constants.deviceNameUnknown,
				onSuccess: async ({ data }) => {
					const member = data.member;

					if (member?.email_verified_at) {
						await _openSession({ member, accessToken: data.authorization.access_token });
						set({ password: '' });
					} else {
						set({ isVerificationDialogVisible: true });
					}

					set({ isLoading: false });
				},
				onError: (error) => {
					const status = statusOf(error);
					const isRejected =
						status === Constants.httpStatusUnprocessableEntity || status === Constants.httpStatusUnauthorized;

					if (isRejected) {
						onRejected?.();
					}

					set({
						isLoading: false,
						error: {
							message: isRejected
								? Translations.loginEmailPasswordCombinationInvalidError
								: Translations.somethingWentWrongTitle,
						},
					});
				},
			});
		};

		const postRegisterAccount = async ({
			onSuccess,
			onError,
		}: {
			onSuccess?: () => void;
			onError?: () => void;
		} = {}) => {
			set({ isLoading: true, error: null });

			const { email, firstName, infix, lastName, newPassword, repeatPassword } = get();

			await useAuthStore.getState().postApiRegisterAccount({
				data: {
					first_name: firstName,
					infix,
					last_name: lastName,
					email,
					password: newPassword,
					password_confirmation: repeatPassword,
				},
				onSuccess: async () => {
					useServicesStore.getState().logEvent({
						name: Constants.analyticsEventSignUp,
						params: { [Constants.analyticsParamScreenPage]: Constants.screenLogin },
						user: { email },
					});

					await useAuthStore.getState().setDidOnboarding(true);

					set({
						isLoading: false,
						error: null,
						newPassword: '',
						repeatPassword: '',
						isRegistrationDoneDialogVisible: true,
						flow: LoginFlowType.Login,
					});

					onSuccess?.();
				},
				onError: (error) => {
					set({ flow: LoginFlowType.Register });
					onError?.();
					set({ isLoading: false, error: validationErrorOf(error) ?? { message: Translations.somethingWentWrongTitle } });
				},
			});
		};

		const getVerifyMagicLink = async ({ magicLink, onInvalid }: { magicLink: string; onInvalid?: () => void }) => {
			set({ isLoading: true, error: null });

			await useAuthStore.getState().getApiVerifyMagicLink({
				magicLink,
				onSuccess: async ({ data }) => {
					const member = data.member;

					if (!member) {
						onInvalid?.();
						set({ isLoading: false, error: { message: Translations.loginMagiclinkInvalidError } });

						return;
					}

					await _openSession({ member, accessToken: data.authorization.access_token });
					set({ isLoading: false, password: '' });
				},
				onError: () => {
					onInvalid?.();
					set({ isLoading: false, error: { message: Translations.loginMagiclinkInvalidError } });
				},
				onFinally: () => set({ isMailSentDialogVisible: false }),
			});
		};

		const getMeWithAccessToken = async ({ accessToken }: { accessToken: string }) => {
			await useAuthStore.getState().getApiMe({
				accessToken,
				onSuccess: async (response) => {
					const member = response.data.member;

					if (!member) {
						set({ isLoading: false, error: { message: Translations.somethingWentWrongTitle } });

						return;
					}

					const auth = useAuthStore.getState();

					await auth.setToken(accessToken);
					await auth.setUser(member);
					await auth.setDidOnboarding(true);
					await _applyLanguagePreference();
					set({ isLoading: false });
				},
				onError: (error) => {
					set({ isLoading: false, error: { message: Translations.somethingWentWrongTitle } });
					useServicesStore
						.getState()
						.captureEvent({ message: 'getApiMe failed on the web token hand-off', extra: { error } });
				},
			});
		};

		const postMagicLink = async () => {
			set({ error: null, isMailSentDialogVisible: true });

			await useAuthStore.getState().postApiMagicLink({
				email: get().email,
				onError: (error) =>
					useServicesStore.getState().captureEvent({ message: 'postApiMagicLink failed', extra: { error } }),
			});
		};

		const postResendVerification = async () => {
			set({ isResendVerificationProcessing: true });

			await useAuthStore.getState().postApiResendVerification({
				email: get().email,
				onSuccess: () =>
					set({
						isResendVerificationProcessing: false,
						isVerificationDialogVisible: false,
						isMailSentDialogVisible: true,
					}),
				onError: (error) => {
					set({ isResendVerificationProcessing: false, error: { message: Translations.somethingWentWrongTitle } });
					useServicesStore.getState().captureEvent({ message: 'postApiResendVerification failed', extra: { error } });
				},
			});
		};

		const postForgotPassword = async () => {
			set({ isResetPasswordProcessing: true });

			await useAuthStore.getState().postApiForgotPassword({
				email: get().email,
				onSuccess: (response) =>
					set({
						resetPasswordSuccessful: response.status === Constants.httpStatusOk,
						isResetPasswordProcessing: false,
					}),
				onError: (error) => {
					set({ resetPasswordSuccessful: false, isResetPasswordProcessing: false });
					useServicesStore.getState().captureEvent({ message: 'postApiForgotPassword failed', extra: { error } });
				},
			});
		};

		// The password step is the last one, so any other target means the typed password is left behind.
		const goToStep = (step: LoginStepType) =>
			set({ step, error: null, ...(step === LoginStepType.Last ? {} : { password: '' }) });

		const setEmail = (email: string) => set({ email });

		const setPassword = (password: string) => set({ password });

		const setGender = (gender: string) => set({ gender });

		const setFirstName = (firstName: string) => set({ firstName });

		const setInfix = (infix: string) => set({ infix });

		const setLastName = (lastName: string) => set({ lastName });

		const setNewPassword = (newPassword: string) =>
			set(newPassword === '' ? { newPassword, repeatPassword: '' } : { newPassword });

		const setRepeatPassword = (repeatPassword: string) => set({ repeatPassword });

		const clearError = () => set({ error: null });

		const setResetPasswordSuccessful = (resetPasswordSuccessful: boolean | null) => set({ resetPasswordSuccessful });

		const setResetPasswordDialogVisible = (isResetPasswordDialogVisible: boolean) =>
			set({ isResetPasswordDialogVisible });

		const setRegistrationDoneDialogVisible = (isRegistrationDoneDialogVisible: boolean) =>
			set({ isRegistrationDoneDialogVisible });

		const setMailSentDialogVisible = (isMailSentDialogVisible: boolean) => set({ isMailSentDialogVisible });

		const setVerificationDialogVisible = (isVerificationDialogVisible: boolean) => set({ isVerificationDialogVisible });

		return {
			init,
			postCheckEmail,
			postLogin,
			postRegisterAccount,
			getVerifyMagicLink,
			getMeWithAccessToken,
			postMagicLink,
			postResendVerification,
			postForgotPassword,
			goToStep,
			setEmail,
			setPassword,
			setGender,
			setFirstName,
			setInfix,
			setLastName,
			setNewPassword,
			setRepeatPassword,
			clearError,
			setResetPasswordSuccessful,
			setResetPasswordDialogVisible,
			setRegistrationDoneDialogVisible,
			setMailSentDialogVisible,
			setVerificationDialogVisible,
		};
	}),
);
